// src/features/audittrail/DsarFormDialog.tsx
// New data subject access request (GDPR Article 15+).

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { audittrailStore } from '@core/stores';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import type { DSARType } from '@core/types/audittrail';
import { AudittrailModal } from './Modal';
import { DSAR_TYPE_VALUES, enumKey, errMessage } from './logic';

interface DsarFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const DsarFormDialog = observer(({ open, onClose, onCreated }: DsarFormDialogProps) => {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [requestType, setRequestType] = useState<DSARType>('DSAR_TYPE_ACCESS');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!email.trim() || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await audittrailStore.CreateDataSubjectAccessRequest({
        subject_email: email.trim(),
        subject_name: name.trim(),
        description: description.trim() || undefined,
        request_type: requestType,
      });
      setEmail(''); setName(''); setDescription('');
      onCreated();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('audittrail.dsar.form.error')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AudittrailModal
      open={open}
      title={t('audittrail.dsar.form.title')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={submit} isLoading={saving} disabled={!email.trim() || !name.trim()}>{t('audittrail.dsar.form.submit')}</Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <ErrorAlert error={error} />}
        <Input type="email" label={t('audittrail.dsar.form.email')} value={email} onChange={(e) => setEmail(e.target.value)} required />
        <Input label={t('audittrail.dsar.form.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Select
          label={t('audittrail.dsar.form.type')}
          value={requestType}
          onChange={(e) => setRequestType(e.target.value as DSARType)}
          options={DSAR_TYPE_VALUES.map((v) => ({ value: v, label: t(`audittrail.dsarType.${enumKey(v, 'DSAR_TYPE_')}`) }))}
        />
        <Textarea label={t('audittrail.dsar.form.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </div>
    </AudittrailModal>
  );
});

export default DsarFormDialog;
